import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import { BASE_URL } from "../../config";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faHospital,
  faUserMd,
  faFileMedical,
  faStethoscope,
  faChartLine,
  faSyncAlt,
  faPlus
} from "@fortawesome/free-solid-svg-icons";

import {
  ArrowLeftIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  DocumentTextIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilSquareIcon,
  ClockIcon,
  FlagIcon,
  DocumentCheckIcon,
  ArrowRightIcon,
  CheckCircleIcon, // Added for Accredit
  XCircleIcon, // Added for Disaccredit
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import { useTable, usePagination } from "react-table";
import clsx from "clsx";
import {
  publishTrackerProviderSelection,
} from "../../utils/trackerBridge";

const Badge = ({ children, color = "gray" }) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    green: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    blue: "bg-blue-100 text-blue-700 ring-blue-200",
    red: "bg-rose-100 text-rose-700 ring-rose-200",
    yellow: "bg-amber-100 text-amber-700 ring-amber-200",
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
        colors[color]
      )}
    >
      {children}
    </span>
  );
};

const EmptyState = ({
  title = "No results",
  subtitle = "Try adjusting your search or filters.",
}) => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-10 text-center">
    <svg
      viewBox="0 0 24 24"
      className="mb-2 h-8 w-8 text-gray-400"
      fill="none"
      stroke="currentColor"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
    <div className="text-sm font-medium text-gray-900">{title}</div>
    <div className="text-xs text-gray-500">{subtitle}</div>
  </div>
);

const ACTION_MENU_WIDTH = 288;
const ACTION_MENU_HEIGHT = 260;
const ACTION_MENU_MARGIN = 12;
const ACTION_MENU_GAP = 8;

const getDownloadFilename = (contentDisposition, fallbackFilename) => {
  if (!contentDisposition) {
    return fallbackFilename;
  }

  const utf8FilenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8FilenameMatch && utf8FilenameMatch[1]) {
    return decodeURIComponent(utf8FilenameMatch[1].replace(/["']/g, "").trim());
  }

  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  if (filenameMatch && filenameMatch[1]) {
    return filenameMatch[1].trim();
  }

  return fallbackFilename;
};

const getActionMenuPosition = (triggerElement) => {
  const rect = triggerElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = rect.right - ACTION_MENU_WIDTH;
  left = Math.max(
    ACTION_MENU_MARGIN,
    Math.min(left, viewportWidth - ACTION_MENU_WIDTH - ACTION_MENU_MARGIN)
  );

  let top = rect.bottom + ACTION_MENU_GAP;
  let placement = "bottom";

  if (top + ACTION_MENU_HEIGHT > viewportHeight - ACTION_MENU_MARGIN) {
    top = rect.top - ACTION_MENU_HEIGHT - ACTION_MENU_GAP;
    placement = "top";
  }

  const maxTop = Math.max(
    ACTION_MENU_MARGIN,
    viewportHeight - ACTION_MENU_HEIGHT - ACTION_MENU_MARGIN
  );
  top = Math.max(ACTION_MENU_MARGIN, Math.min(top, maxTop));

  return { top, left, placement };
};

const ProviderTable = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("provider_code");
  const [sortDirection, setSortDirection] = useState("asc");

  const [accreditationFilter, setAccreditationFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [movementModalOpen, setMovementModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [movementData, setMovementData] = useState([]);
  const [movementLoading, setMovementLoading] = useState(false);
  const [movementCurrentPage, setMovementCurrentPage] = useState(1);
  const [movementItemsPerPage, setMovementItemsPerPage] = useState(5);
  const [movementTotalCount, setMovementTotalCount] = useState(0);

  const [showAccreditationModal, setShowAccreditationModal] = useState(false);
  const [selectedProviderForAccreditation, setSelectedProviderForAccreditation] = useState(null);
  const [isAccrediting, setIsAccrediting] = useState(false);
  const [downloadingAffiliationsProviderCode, setDownloadingAffiliationsProviderCode] = useState("");
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);

  const getAuthHeaders = useCallback(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
    }),
    []
  );

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/providers`, {
        headers: getAuthHeaders(),
        params: {
          limit_offset: (currentPage - 1) * itemsPerPage,
          search_string: searchQuery,
          sort_by: sortBy,
          sort_direction: sortDirection,
          limit: itemsPerPage,
          accreditation_flag: accreditationFilter === "ALL" ? undefined : accreditationFilter,
          status_flag: statusFilter === "ALL" ? undefined : statusFilter,
        },
      });
      setData(response.data.providers || []);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      toast.error("Failed to fetch providers.", { autoClose: 3000 });
      setTimeout(() => navigate("/"), 2000);
    } finally {
      setLoading(false);
    }
  };

  const fetchProviderMovement = async (providerCode) => {
    setMovementLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/providers/${providerCode}/movement`,
        {
          headers: getAuthHeaders(),
          params: {
            page: movementCurrentPage,
            per_page: movementItemsPerPage,
            sort_by: "lastupdate_date",
            sort_direction: "desc",
          },
        }
      );
      setMovementData(response.data.data.movements || response.data.data.batches || []);
      setMovementTotalCount(response.data.data.total || 0);
    } catch (error) {
      toast.error("Failed to fetch provider movement.", { autoClose: 3000 });
    } finally {
      setMovementLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, [currentPage, itemsPerPage, searchQuery, sortBy, sortDirection, accreditationFilter, statusFilter]);

  useEffect(() => {
    if (selectedProvider) {
      fetchProviderMovement(selectedProvider.provider_code);
    }
  }, [selectedProvider, movementCurrentPage, movementItemsPerPage]);

  const handleDownloadAffiliatedPhysicians = useCallback(
    async (provider) => {
      const providerCode = String(provider?.provider_code || "").trim();

      if (!providerCode) {
        toast.error("Provider code is missing.", { autoClose: 3000 });
        return;
      }

      if (downloadingAffiliationsProviderCode === providerCode) {
        return;
      }

      setDownloadingAffiliationsProviderCode(providerCode);

      try {
        const response = await axios.get(
          `${BASE_URL}/providers/${encodeURIComponent(
            providerCode
          )}/export/physician-affiliations/csv`,
          {
            headers: getAuthHeaders(),
            responseType: "blob",
          }
        );

        const fallbackFilename = `${providerCode}_physician_affiliations.csv`;
        const filename = getDownloadFilename(
          response.headers["content-disposition"],
          fallbackFilename
        );

        const blob = new Blob([response.data], {
          type: response.headers["content-type"] || "text/csv;charset=utf-8",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        toast.success("Physician affiliations exported successfully.", {
          autoClose: 3000,
        });
      } catch (error) {
        toast.error("Failed to export physician affiliations.", {
          autoClose: 3000,
        });
      } finally {
        setDownloadingAffiliationsProviderCode("");
      }
    },
    [downloadingAffiliationsProviderCode, getAuthHeaders]
  );

  const closeActionMenu = useCallback(() => {
    setOpenActionMenu(null);
    setActionMenuPosition(null);
  }, []);

  useEffect(() => {
    if (!openActionMenu) return;

    const handleViewportChange = () => closeActionMenu();
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeActionMenu();
      }
    };

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [closeActionMenu, openActionMenu]);

  const getRowActionKey = (provider) => provider?.provider_code || provider?.tbl_id;

  const toggleActionMenu = useCallback(
    (provider, event) => {
      const rowKey = getRowActionKey(provider);
      if (openActionMenu === rowKey) {
        closeActionMenu();
        return;
      }

      setOpenActionMenu(rowKey);
      setActionMenuPosition(getActionMenuPosition(event.currentTarget));
    },
    [closeActionMenu, openActionMenu]
  );

  const handleUseProviderInTracker = useCallback((provider, event) => {
    event?.stopPropagation?.();
    publishTrackerProviderSelection({
      provider_code: provider?.provider_code,
      provider_name: provider?.provider_name,
    });
    toast.success("Provider sent to Tracker.");
  }, []);

  const getRowActions = useCallback(
    (provider) => {
      const actions = [
        {
          key: "tracker",
          label: "Use in Tracker",
          icon: ArrowRightIcon,
          color: "text-emerald-600",
          onClick: () => handleUseProviderInTracker(provider),
        },
        {
          key: "view",
          label: "View Provider",
          icon: EyeIcon,
          color: "text-blue-600",
          onClick: () =>
            navigate(`/medical/view-provider/${provider.provider_code}`),
        },
        {
          key: "edit",
          label: "Edit Provider",
          icon: PencilSquareIcon,
          color: "text-amber-600",
          onClick: () =>
            navigate(`/medical/update-provider/${provider.provider_code}`),
        },
        {
          key: "movement",
          label: "View Movement",
          icon: ClockIcon,
          color: "text-indigo-600",
          onClick: () => {
            setMovementCurrentPage(1);
            setSelectedProvider(provider);
            setMovementModalOpen(true);
          },
        },
        {
          key: "accreditation",
          label:
            provider.accreditation_flag == "0"
              ? "Disaccredit Provider"
              : "Accredit Provider",
          icon: provider.accreditation_flag == "0" ? XCircleIcon : CheckCircleIcon,
          color:
            provider.accreditation_flag == "0"
              ? "text-rose-600"
              : "text-emerald-600",
          onClick: () => {
            setSelectedProviderForAccreditation(provider);
            setShowAccreditationModal(true);
          },
        },
        {
          key: "finalize",
          label: "Finalize",
          icon: DocumentCheckIcon,
          color: "text-violet-600",
          onClick: () => {},
        },
        {
          key: "download_physicians",
          label:
            downloadingAffiliationsProviderCode ===
            String(provider?.provider_code || "")
              ? "Downloading Affiliated Physicians..."
              : "Download Affiliated Physicians",
          icon: ArrowDownTrayIcon,
          color: "text-cyan-600",
          onClick: () => handleDownloadAffiliatedPhysicians(provider),
        }
      ];

      return actions;
    },
    [
      downloadingAffiliationsProviderCode,
      handleDownloadAffiliatedPhysicians,
      handleUseProviderInTracker,
      navigate,
    ]
  );

  const accreditationStatusMap = {
    0: { name: "Accredited", color: "green" },
    1: { name: "Disaccredited", color: "red" },
    2: { name: "Non-accredited", color: "yellow" },
    default: { name: "N/A", color: "gray" },
  };

  const statusMap = {
    1: { name: "Active", color: "green" },
    0: { name: "Inactive", color: "red" },
    2: { name: "Terminated/Cancelled", color: "slate" },
    3: { name: "Suspended", color: "yellow" },
    5: { name: "Extension", color: "blue" },
    default: { name: "Unknown", color: "gray" },
  };

  const columns = useMemo(
    () => [
      {
        Header: "Provider Code",
        accessor: "provider_code",
        Cell: ({ value, row }) => (
          <span className="inline-flex items-center gap-2 font-medium text-gray-900">
            <span>{value || "N/A"}</span>
            <button
              type="button"
              aria-label={`Use provider ${value || ""} in Tracker`}
              title="Use in Tracker"
              onClick={(event) => handleUseProviderInTracker(row.original, event)}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-emerald-100 hover:shadow focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-1"
            >
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          </span>
        ),
      },
      {
        Header: "Provider Name",
        accessor: "provider_name",
        Cell: ({ value }) => <span className="text-gray-900">{value || "N/A"}</span>,
      },
      {
        Header: "Location",
        accessor: "location",
        Cell: ({ value }) => <span className="text-gray-600">{value || "N/A"}</span>,
      },
      {
        Header: "Accreditation Status",
        accessor: "accreditation_flag",
        Cell: ({ value }) => {
          const status = accreditationStatusMap[value] || accreditationStatusMap.default;
          return <Badge color={status.color}>{status.name}</Badge>;
        },
      },
      {
        Header: "Status",
        accessor: "status_flag",
        Cell: ({ value }) => {
          const status = statusMap[value] || statusMap.default;
          return <Badge color={status.color}>{status.name}</Badge>;
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => {
          const rowData = row.original;
          const rowKey = getRowActionKey(rowData);
          const rowActions = getRowActions(rowData);

          return (
            <div data-provider-action-root="true" className="relative">
              <button
                type="button"
                aria-label={`Actions for provider ${rowData?.provider_code || ""}`}
                onClick={(event) => toggleActionMenu(rowData, event)}
                className={clsx(
                  "inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold shadow-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1",
                  openActionMenu === rowKey
                    ? "border-red-400 bg-red-50 text-red-700 shadow-red-100"
                    : "border-gray-300 bg-white text-gray-700 hover:-translate-y-0.5 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                )}
              >
                <EllipsisVerticalIcon className="h-4 w-4 text-gray-500" />
                <span>Actions</span>
              </button>

              {openActionMenu === rowKey &&
                actionMenuPosition &&
                typeof document !== "undefined" &&
                createPortal(
                  <>
                    <button
                      type="button"
                      aria-label="Close provider actions menu"
                      className="fixed inset-0 z-[9998] cursor-default bg-transparent"
                      onClick={closeActionMenu}
                    />
                    <div
                      className="fixed z-[9999] w-72 overflow-visible"
                      style={{
                        top: `${actionMenuPosition.top}px`,
                        left: `${actionMenuPosition.left}px`,
                      }}
                    >
                      <div
                        className={clsx(
                          "absolute right-6 h-3 w-3 rotate-45 border bg-white",
                          actionMenuPosition.placement === "top"
                            ? "-bottom-1.5 border-l-0 border-t-0 border-gray-200"
                            : "-top-1.5 border-b-0 border-r-0 border-gray-200"
                        )}
                      />
                      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl shadow-slate-900/15 ring-1 ring-black/5">
                        <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Provider Actions
                          </p>
                        </div>
                        <div className="space-y-1 p-2">
                          {rowActions.map((action) => {
                            const ActionIcon = action.icon;
                            return (
                              <button
                                key={action.key}
                                type="button"
                                onClick={() => {
                                  action.onClick?.();
                                  closeActionMenu();
                                }}
                                className="group flex w-full items-center justify-between rounded-lg border border-transparent px-3 py-2.5 text-left text-sm text-gray-700 transition-all duration-150 hover:translate-x-0.5 hover:border-red-100 hover:bg-gradient-to-r hover:from-red-50 hover:to-rose-50 hover:text-red-700 hover:shadow-sm"
                              >
                                <span className="flex min-w-0 items-center">
                                  <span className="mr-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 transition-all duration-150 group-hover:bg-white group-hover:shadow-sm">
                                    <ActionIcon className={clsx("h-4 w-4", action.color)} />
                                  </span>
                                  <span className="truncate font-medium">
                                    {action.label}
                                  </span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </>,
                  document.body
                )}
            </div>
          );
        },
      },
    ],
    [
      actionMenuPosition,
      closeActionMenu,
      getRowActions,
      handleUseProviderInTracker,
      openActionMenu,
      toggleActionMenu,
    ]
  );

  const movementColumns = useMemo(
    () => [
      {
        Header: "Update For",
        accessor: "provider_flag",
        Cell: ({ value }) => {
          const flag = String(value ?? "").toLowerCase();
          const mapping = {
            reactivation: { label: "Activation/Reactivation", color: "green" },
            termination: { label: "Termination/Cancellation", color: "red" },
            suspension: { label: "Suspension", color: "yellow" },
            extension: { label: "Extension", color: "slate" },
            bank_update: { label: "Bank Update", color: "blue" },
            "0": { label: "Pending", color: "gray" },
          };
          const status = mapping[flag] || { label: "N/A", color: "gray" };
          return <Badge color={status.color}>{status.label}</Badge>;
        },
      },
      {
        Header: "Effective Date",
        accessor: "effectivity_date",
        Cell: ({ value }) =>
          value ? new Date(value).toLocaleDateString("en-PH") : "N/A",
      },
      {
        Header: "Updated By",
        accessor: "update_by",
        Cell: ({ value }) => <span className="text-gray-700">{value || "N/A"}</span>,
      },
      {
        Header: "Reason",
        accessor: "reason",
        Cell: ({ value }) => <span className="text-gray-700">{value || "N/A"}</span>,
      },
      {
        Header: "Remarks",
        accessor: "remarks",
        Cell: ({ value }) => <span className="text-gray-700">{value || "N/A"}</span>,
      },
      {
        Header: "Last Update",
        accessor: "lastupdate_date",
        Cell: ({ value }) =>
          value ? new Date(value).toLocaleString("en-PH") : "N/A",
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: currentPage - 1, pageSize: itemsPerPage },
      manualPagination: true,
      pageCount: Math.ceil(totalCount / itemsPerPage),
    },
    usePagination
  );

  const {
    getTableProps: getMovementTableProps,
    getTableBodyProps: getMovementTableBodyProps,
    headerGroups: movementHeaderGroups,
    rows: movementRows,
    prepareRow: prepareMovementRow,
  } = useTable(
    {
      columns: movementColumns,
      data: movementData,
      initialState: { pageIndex: movementCurrentPage - 1, pageSize: movementItemsPerPage },
      manualPagination: true,
      pageCount: Math.ceil(movementTotalCount / movementItemsPerPage),
    },
    usePagination
  );

  const handleSort = (column) => {
    if (!column || !column.id) return;
    if (sortBy === column.id) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column.id);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  const movementTotalPages = Math.max(1, Math.ceil(movementTotalCount / movementItemsPerPage));
  const getMovementPageNumbers = () => {
    const maxPagesToShow = 5;
    const startPage = Math.max(1, movementCurrentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(movementTotalPages, startPage + maxPagesToShow - 1);
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/providers/export`, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "providers.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Providers exported successfully.");
    } catch (error) {
      toast.error("Failed to export providers.");
    }
  };

  const handleAccreditationToggle = async () => {
    setIsAccrediting(true);
    try {
      const newFlag = selectedProviderForAccreditation.accreditation_flag == "0" ? "1" : "0";
      await axios.put(
        `${BASE_URL}/providers/${selectedProviderForAccreditation.provider_code}/accreditation`,
        { accreditation_flag: newFlag },
        { headers: getAuthHeaders() }
      );
      toast.success(`Provider ${newFlag === "0" ? "accredited" : "disaccredited"} successfully!`);
      fetchProviders();
      setShowAccreditationModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update accreditation status.");
    } finally {
      setIsAccrediting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-1xl p-4">
      <ToastContainer />

      {/* Toolbar */}
      <div className="mb-4 rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-white/10 p-2 ring-1 ring-white/20 backdrop-blur">
              <FontAwesomeIcon icon={faHospital} className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-base font-semibold">Providers (Hospitals & Clinics)</h1>
              <div className="text-xs text-white/90">
                {totalCount.toLocaleString()} total record{totalCount === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate("/medical/add-new-provider")}
              className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/20 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Add New</span>
            </button>
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/20 hover:bg-white/20"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              <span>Export</span>
            </button>
          </div>
        </div>

        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search providers..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <select
            value={accreditationFilter}
            onChange={(e) => {
              setAccreditationFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="rounded-md border border-gray-200 px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="ALL">All Accreditation Statuses</option>
            <option value="0">Accredited</option>
            <option value="1">Disaccredited</option>
            <option value="2">Non-accredited</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="rounded-md border border-gray-200 px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="ALL">All Statuses</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
            <option value="2">Terminated/Cancelled</option>
            <option value="3">Suspended</option>
            <option value="5">Extension</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="max-h-[60vh] overflow-auto">
          {loading ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 bg-gray-50">
                {headerGroups.map((headerGroup, i) => (
                  <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        key={column.id}
                        {...column.getHeaderProps()}
                        onClick={() => handleSort(column)}
                        className={clsx(
                          "px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600",
                          "cursor-pointer select-none"
                        )}
                      >
                        <div className="inline-flex items-center gap-1">
                          {column.render("Header")}
                          {column.id === sortBy && (
                            <span className="text-gray-400">
                              {sortDirection === "asc" ? "▲" : "▼"}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {[...Array(itemsPerPage)].map((_, idx) => (
                  <tr key={idx} className="animate-pulse border-t">
                    {[...Array(columns.length)].map((__, i) => (
                      <td key={i} className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : data.length === 0 ? (
            <div className="p-8">
              <EmptyState title="No providers found" subtitle="Try changing your filters or search." />
            </div>
          ) : (
            <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 bg-gray-50">
                {headerGroups.map((headerGroup, i) => (
                  <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        key={column.id}
                        {...column.getHeaderProps()}
                        onClick={() => handleSort(column)}
                        className={clsx(
                          "px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600",
                          "cursor-pointer select-none"
                        )}
                      >
                        <div className="inline-flex items-center gap-1">
                          {column.render("Header")}
                          {column.id === sortBy && (
                            <span className="text-gray-400">
                              {sortDirection === "asc" ? "▲" : "▼"}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="divide-y divide-gray-100">
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} className="hover:bg-gray-50 transition-colors">
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()} className="px-4 py-3 text-sm text-gray-800">
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 p-3 text-sm text-gray-700 sm:flex-row">
        <div className="text-xs text-gray-600">
          Showing {totalCount ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount.toLocaleString()} entries
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === 1 ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            <ChevronDoubleLeftIcon className="h-5 w-5" />
            First
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === 1 ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            <ChevronLeftIcon className="h-5 w-5" />
            Previous
          </button>
          {getPageNumbers().map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={clsx(
                "rounded-md px-2 py-1 text-xs",
                currentPage === page ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === totalPages ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            Next
            <ChevronRightIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === totalPages ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            Last
            <ChevronDoubleRightIcon className="h-5 w-5" />
          </button>

          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="ml-1 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Movement Modal */}
      {movementModalOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setMovementModalOpen(false)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl overflow-hidden"
          >
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white flex justify-between items-center">
              <h2 className="text-base font-semibold">
                Provider Movement History of {selectedProvider?.provider_name}
              </h2>
              <button
                onClick={() => setMovementModalOpen(false)}
                className="text-white/80 hover:text-white"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 max-h-[60vh] overflow-auto">
              {movementLoading ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    {movementHeaderGroups.map((headerGroup, i) => (
                      <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th
                            key={column.id}
                            {...column.getHeaderProps()}
                            className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                          >
                            {column.render("Header")}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {[...Array(movementItemsPerPage)].map((_, idx) => (
                      <tr key={idx} className="animate-pulse border-t">
                        {[...Array(movementColumns.length)].map((__, i) => (
                          <td key={i} className="px-4 py-3">
                            <div className="h-4 bg-gray-200 rounded" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : movementData.length === 0 ? (
                <EmptyState title="No movements found" subtitle="No provider movement history available for this provider." />
              ) : (
                <table {...getMovementTableProps()} className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    {movementHeaderGroups.map((headerGroup, i) => (
                      <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th
                            key={column.id}
                            {...column.getHeaderProps()}
                            className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                          >
                            {column.render("Header")}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getMovementTableBodyProps()} className="divide-y divide-gray-100">
                    {movementRows.map((row) => {
                      prepareMovementRow(row);
                      return (
                        <tr {...row.getRowProps()} className="hover:bg-gray-50 transition-colors">
                          {row.cells.map((cell) => (
                            <td {...cell.getCellProps()} className="px-4 py-3 text-sm text-gray-800">
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Movement Pagination */}
            <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 p-3 text-sm text-gray-700 sm:flex-row">
              <div className="text-xs text-gray-600">
                Showing {movementTotalCount ? (movementCurrentPage - 1) * movementItemsPerPage + 1 : 0} to {Math.min(movementCurrentPage * movementItemsPerPage, movementTotalCount)} of {movementTotalCount.toLocaleString()} entries
              </div>
              <div className="flex flex-wrap items-center gap-1">
                <button
                  onClick={() => setMovementCurrentPage(1)}
                  disabled={movementCurrentPage === 1}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    movementCurrentPage === 1 ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  <ChevronDoubleLeftIcon className="h-5 w-5" />
                  First
                </button>
                <button
                  onClick={() => setMovementCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={movementCurrentPage === 1}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    movementCurrentPage === 1 ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                  Previous
                </button>
                {getMovementPageNumbers().map((page) => (
                  <button
                    key={page}
                    onClick={() => setMovementCurrentPage(page)}
                    className={clsx(
                      "rounded-md px-2 py-1 text-xs",
                      movementCurrentPage === page ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    )}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setMovementCurrentPage((p) => Math.min(movementTotalPages, p + 1))}
                  disabled={movementCurrentPage === movementTotalPages}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    movementCurrentPage === movementTotalPages ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  Next
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setMovementCurrentPage(movementTotalPages)}
                  disabled={movementCurrentPage === movementTotalPages}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    movementCurrentPage === movementTotalPages ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  Last
                  <ChevronDoubleRightIcon className="h-5 w-5" />
                </button>

                <select
                  value={movementItemsPerPage}
                  onChange={(e) => {
                    setMovementItemsPerPage(Number(e.target.value));
                    setMovementCurrentPage(1);
                  }}
                  className="ml-1 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  {[5, 10, 20, 50].map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Accreditation Confirmation Modal */}
      {showAccreditationModal && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowAccreditationModal(false)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
          >
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
              <h2 className="text-base font-semibold">
                Confirm {selectedProviderForAccreditation.accreditation_flag == "0" ? "Disaccreditation" : "Accreditation"}
              </h2>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-700 mb-4">
                Are you sure you want to {selectedProviderForAccreditation.accreditation_flag == "0" ? "disaccredit" : "accredit"} this provider: {selectedProviderForAccreditation.provider_name}?
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAccreditationModal(false)}
                  disabled={isAccrediting}
                  className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-800 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAccreditationToggle}
                  disabled={isAccrediting}
                  className={clsx(
                    "inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white hover:opacity-90",
                    selectedProviderForAccreditation.accreditation_flag == "0"
                      ? "bg-rose-600"
                      : "bg-emerald-600"
                  )}
                >
                  {isAccrediting ? (
                    <>
                      <svg
                        className="h-5 w-5 animate-spin text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Processing…
                    </>
                  ) : (
                    <>
                      {selectedProviderForAccreditation.accreditation_flag == "0" ? (
                        <XCircleIcon className="h-5 w-5" />
                      ) : (
                        <CheckCircleIcon className="h-5 w-5" />
                      )}
                      Yes
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default ProviderTable;



